import { useCallback, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { usePersonChangeHomeArea } from '@/features/api/hooks/participant/usePersonChangeHomeArea';
import { useChangeSelectedGeoAreas } from '@/features/api/hooks/shared-app/useChangeSelectedGeoAreas';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { GeoAreaSubscription } from '@/features/geo-area/components/subscription/GeoAreaSubscription';
import { Terms } from '@/features/privacy/components/Terms';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';
import { StateView } from '@/shared/components/state-view/StateView';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import { EditMode } from '@/types';

type Props = AppNativeStackScreenProps<typeof AppRoute.RequiredAction>;

export const RequiredActionScreen: FC<Props> = () => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.AUTHORIZATION, {
    keyPrefix: 'screens.registration'
  });

  const {
    homeGeoAreaId,
    hasHomeGeoArea,
    hasAcceptedTerms,
    isOnboardingStatusRefetching,
    updateAppStates
  } = useAppState();

  const handleUpdateChanges = useCallback(() => {
    // updating app state will automatically navigate
    updateAppStates();
  }, [updateAppStates]);

  const { mutate: changeHomeArea } = usePersonChangeHomeArea();
  const { mutate: changeGeoAreas } = useChangeSelectedGeoAreas();

  const handleGeoAreaChange = useCallback(
    (id?: string, ids: string[] = []) => {
      if (isEmptyOrNull(id)) return;
      changeHomeArea(
        { newHomeAreaId: id },
        {
          onSuccess: () =>
            changeGeoAreas(
              {
                newSelectedGeoAreaIds: [id, ...ids]
              },
              { onSuccess: handleUpdateChanges }
            )
        }
      );
    },
    [changeGeoAreas, changeHomeArea, handleUpdateChanges]
  );

  if (isOnboardingStatusRefetching)
    return (
      <StateView isLoading backgroundColor={theme.colors.surfaceContainerLow} />
    );

  if (!hasHomeGeoArea) {
    return (
      <GeoAreaSubscription
        selectedGeoAreaId={homeGeoAreaId}
        selectedGeoAreaIds={[]}
        onChange={handleGeoAreaChange}
        labels={t('geo-area', { returnObjects: true })}
        stickyButton
      />
    );
  }

  if (!hasAcceptedTerms) {
    return (
      <Terms
        mode={EditMode.REQUIRED_ACTION}
        onAccept={handleUpdateChanges}
        stickyButton
      />
    );
  }
};
